import { useState } from 'react'
import type { FormEvent } from 'react'
import { Dialogo } from './Dialogo'
import { cantidad as formatearCantidad, dinero } from '../api/formato'

interface Props {
    nombre: string
    unidad: string
    decimales: number
    precioUnitario: number
    cantidadInicial?: number
    alConfirmar: (cantidad: number) => void
    alCerrar: () => void
}

/**
 * Ingreso manual de cantidad o peso.
 *
 * En esta etapa el peso lo teclea el cajero: no se lee de la balanza ni de la
 * etiqueta. Por eso el dialogo muestra el total en vivo, que es la unica forma
 * de que note un error de tipeo antes de cobrar.
 */
export function DialogoCantidad({
    nombre,
    unidad,
    decimales,
    precioUnitario,
    cantidadInicial,
    alConfirmar,
    alCerrar,
}: Props) {
    const [texto, setTexto] = useState(cantidadInicial ? String(cantidadInicial) : '')

    // Se acepta coma decimal: es lo que el cajero tiene en la cabeza y en el
    // teclado numerico del teléfono.
    const valor = Number.parseFloat(texto.replace(',', '.'))
    const valido = Number.isFinite(valor) && valor > 0
    const total = valido ? Math.round(valor * precioUnitario) : 0

    const decimalesDeMas =
        valido && decimales === 0 && !Number.isInteger(valor)

    function enviar(evento: FormEvent) {
        evento.preventDefault()
        if (valido && !decimalesDeMas) alConfirmar(valor)
    }

    return (
        <Dialogo
            titulo={decimales > 0 ? 'Ingresar peso' : 'Cambiar cantidad'}
            alCerrar={alCerrar}
            ancho="chico"
            acciones={
                <>
                    <button type="button" className="boton" onClick={alCerrar}>
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        form="forma-cantidad"
                        className="boton boton-primario"
                        disabled={!valido || decimalesDeMas}
                    >
                        Agregar
                    </button>
                </>
            }
        >
            <form id="forma-cantidad" className="columna g4" onSubmit={enviar}>
                <div className="columna g1">
                    <strong>{nombre}</strong>
                    <span className="tenue chico">
                        {dinero(precioUnitario)} por {unidad}
                    </span>
                </div>

                <div>
                    <label className="etiqueta" htmlFor="cantidad">
                        {decimales > 0 ? `Peso en ${unidad}` : 'Cantidad'}
                    </label>
                    <input
                        id="cantidad"
                        className="campo campo-numero"
                        style={{ fontSize: '1.5rem', minHeight: '3.5rem' }}
                        value={texto}
                        onChange={(evento) => setTexto(evento.target.value)}
                        // decimal abre el teclado numerico con coma en el movil.
                        inputMode="decimal"
                        autoComplete="off"
                        placeholder={decimales > 0 ? '0,000' : '0'}
                        required
                    />
                </div>

                {decimalesDeMas && (
                    <p className="aviso aviso-error diminuto" role="alert">
                        Este producto se vende por unidad: la cantidad debe ser entera.
                    </p>
                )}

                <div className="fila entre panel" style={{ padding: 'var(--e3)' }}>
                    <span className="tenue">Total de la línea</span>
                    <strong className="numero" style={{ fontSize: '1.25rem' }}>
                        {dinero(total)}
                    </strong>
                </div>

                {valido && decimales > 0 && (
                    <p className="tenue diminuto derecha">
                        {formatearCantidad(valor, decimales)} {unidad} × {dinero(precioUnitario)}
                    </p>
                )}
            </form>
        </Dialogo>
    )
}
